/**
 * Chord Model Migration Utilities
 *
 * This module provides functions to convert between V1 (string-based) and V2 (Note-based)
 * chord model formats, along with type guards and comparison utilities.
 */
import { isNote } from './noteTypes.js';
import { parseNoteNullable } from './noteMigration.js';
import { formatNoteNullable } from './noteFormatting.js';
import { equalNotesNullable } from './noteComparison.js';
import { normalizeAlterationSymbol } from './enharmonicUtils.js';
// ============================================================================
// Type Guards
// ============================================================================
/**
 * Type guard to check if input is a string (V1 chord format).
 *
 * Used to detect string-based chord inputs for backward compatibility.
 *
 * @param input - Value to check
 * @returns true if input is a string, false otherwise
 *
 * @example
 * if (isChordString(chord)) {
 *   // TypeScript knows chord is string here
 *   const parsed = parseChordString(chord);
 * }
 */
export function isChordString(input) {
    return typeof input === 'string';
}
/**
 * Type guard to check if a chord model is V2 format.
 *
 * Checks if the chord uses Note objects (V2) instead of strings (V1)
 * for fundamental and/or bass fields.
 *
 * @param chord - Chord model to check
 * @returns true if chord uses Note objects (V2), false if strings (V1)
 *
 * @example
 * if (isChordModelV2(chord)) {
 *   // TypeScript knows chord is ChordObjectModelV2 here
 *   console.log(chord.fundamental.noteName);
 * }
 */
export function isChordModelV2(chord) {
    // Handle null/undefined
    if (!chord || typeof chord !== 'object') {
        return false;
    }
    const obj = chord;
    // Check if fundamental is a Note object (or null)
    if (obj.fundamental !== null && obj.fundamental !== undefined) {
        if (typeof obj.fundamental === 'object' && isNote(obj.fundamental)) {
            return true;
        }
    }
    // Check if bass is a Note object (or null)
    if (obj.bass !== null && obj.bass !== undefined) {
        if (typeof obj.bass === 'object' && isNote(obj.bass)) {
            return true;
        }
    }
    // If both are null, we can't determine the version from the data alone.
    // Default to V1 (string-based) for backward compatibility.
    return false;
}
/**
 * Type guard to check if a chord model is V1 format.
 *
 * Checks if the chord uses strings (V1) instead of Note objects (V2)
 * for fundamental and/or bass fields.
 *
 * @param chord - Chord model to check
 * @returns true if chord uses strings (V1), false if Note objects (V2)
 *
 * @example
 * if (isChordModelV1(chord)) {
 *   // TypeScript knows chord is ChordObjectModelV1 here
 *   console.log(chord.fundamental); // string | null
 * }
 */
export function isChordModelV1(chord) {
    // Handle null/undefined
    if (!chord || typeof chord !== 'object') {
        return false;
    }
    const obj = chord;
    // Must have fundamental field
    if (!('fundamental' in obj)) {
        return false;
    }
    // If fundamental is a Note object, it's V2
    if (obj.fundamental !== null && obj.fundamental !== undefined) {
        if (typeof obj.fundamental === 'object' && isNote(obj.fundamental)) {
            return false;
        }
    }
    // If bass is a Note object, it's V2
    if (obj.bass !== null && obj.bass !== undefined) {
        if (typeof obj.bass === 'object' && isNote(obj.bass)) {
            return false;
        }
    }
    // If fundamental is a string or null, and bass is a string or null, it's V1
    if ((obj.fundamental === null || typeof obj.fundamental === 'string') &&
        (obj.bass === null || obj.bass === undefined || typeof obj.bass === 'string')) {
        return true;
    }
    return false;
}
// ============================================================================
// Migration Functions
// ============================================================================
/**
 * Migrate a V1 chord model to V2 format.
 *
 * Converts string-based fundamental and bass notes to Note objects.
 * All other fields are preserved unchanged.
 *
 * @param v1Chord - V1 format chord model
 * @returns V2 format chord model
 * @throws Error if fundamental or bass strings are invalid
 *
 * @example
 * const v1 = { fundamental: "F♯", bass: "C♯", quality: "minor", ... };
 * const v2 = migrateChordToV2(v1);
 * // v2.fundamental = { noteName: 'F', modifier: 's' }
 * // v2.bass = { noteName: 'C', modifier: 's' }
 */
export function migrateChordToV2(v1Chord) {
    // Parse fundamental (may throw if invalid)
    const fundamental = parseNoteNullable(v1Chord.fundamental);
    // Parse bass (may throw if invalid)
    // Handle "-" as null (legacy placeholder for "no bass")
    const bassString = v1Chord.bass === '-' ? null : v1Chord.bass;
    const bass = parseNoteNullable(bassString);
    return {
        fundamental,
        quality: v1Chord.quality,
        extension: v1Chord.extension,
        isDominant: v1Chord.isDominant,
        adds: v1Chord.adds ? [...v1Chord.adds] : [], // Copy array or default to empty
        alterations: v1Chord.alterations ? [...v1Chord.alterations] : [], // Copy array or default to empty
        bass,
    };
}
/**
 * Convert a V2 chord model to V1 format.
 *
 * Converts Note objects back to strings for backward compatibility.
 * Useful for serialization or working with legacy code.
 *
 * @param v2Chord - V2 format chord model
 * @returns V1 format chord model
 *
 * @example
 * const v2 = { fundamental: { noteName: 'F', modifier: 's' }, ... };
 * const v1 = convertChordToV1(v2);
 * // v1.fundamental = "F♯"
 */
export function convertChordToV1(chord) {
    // Handle V3 chords (root instead of fundamental)
    const noteField = 'root' in chord ? chord.root : chord.fundamental;
    return {
        fundamental: formatNoteNullable(noteField),
        quality: chord.quality,
        extension: chord.extension,
        isDominant: chord.isDominant,
        adds: [...chord.adds],
        alterations: [...chord.alterations],
        bass: formatNoteNullable(chord.bass),
    };
}
/**
 * Type guard to check if a chord model is V3 format.
 *
 * V3 chords use `root` instead of `fundamental`.
 *
 * @param chord - Chord model to check
 * @returns true if chord is V3 format
 */
export function isChordModelV3(chord) {
    if (!chord || typeof chord !== 'object') {
        return false;
    }
    const obj = chord;
    return 'root' in obj && !('fundamental' in obj);
}
/**
 * Migrate a quality value from V2 (string | null) to V3 (ChordQuality | null).
 *
 * Converts `""` to `"major"` and validates known quality values.
 * Unknown quality strings are cast to ChordQuality for resilience.
 *
 * @param quality - V2 quality string
 * @returns V3 ChordQuality value
 */
export function migrateQualityToV3(quality) {
    if (quality === null)
        return null;
    if (quality === '' || quality === 'major')
        return 'major';
    return quality;
}
/**
 * Migrate a V2 chord model to V3 format.
 *
 * Converts:
 * - `fundamental` → `root`
 * - `quality: ""` → `quality: "major"`
 * - `extension: "-"` → `extension: null`
 * - Filters `"-"` from adds/alterations arrays
 *
 * Also handles the hybrid case where a chord already uses `root` instead of
 * `fundamental` (e.g., RN stored V2 wrapper with V3 chord internals).
 *
 * @param v2Chord - V2 format chord model (or hybrid with root)
 * @returns V3 format chord model
 */
export function migrateChordToV3(v2Chord) {
    // Handle hybrid: chord may already have `root` instead of `fundamental`
    // (RN app used V3 types internally but saved under V2 wrapper)
    const root = v2Chord.fundamental ?? v2Chord.root ?? null;
    return {
        root,
        quality: migrateQualityToV3(v2Chord.quality),
        extension: v2Chord.extension === '-' ? null : v2Chord.extension,
        isDominant: v2Chord.isDominant,
        adds: (v2Chord.adds ?? []).filter(a => a !== '-'),
        alterations: (v2Chord.alterations ?? []).filter(a => a !== '-'),
        bass: v2Chord.bass,
    };
}
/**
 * Normalize any chord model to V3 format.
 *
 * If already V3, returns as-is. If V1, migrates to V2 first, then to V3.
 * If V2, migrates directly to V3.
 *
 * @param chord - Chord model in any format
 * @returns V3 format chord model
 */
export function normalizeToV3(chord) {
    if (isChordModelV3(chord)) {
        // Ensure required array fields are initialized (external data may have nulls)
        const v3 = chord;
        return enforceChordInvariants({
            ...v3,
            adds: v3.adds ?? [],
            alterations: v3.alterations ?? [],
        });
    }
    if (isChordModelV1(chord))
        return enforceChordInvariants(migrateChordToV3(migrateChordToV2(chord)));
    return enforceChordInvariants(migrateChordToV3(chord));
}
/**
 * Enforce V3 invariants that the UI guarantees (via getDisabledExtensions)
 * but programmatic construction can violate (KNOWN_BUGS.md Bugs 3 & 4):
 * - 6th chords are never dominant ("C6" has a major 6th, not a flat 7th).
 * - Non-dominant sus chords carry no extension (no string representation
 *   exists for them; sus extensions are dominant-flavored, e.g. "C9sus4").
 * - Alterations use Unicode accidentals (♭5, ♯9) — ASCII spellings (b5, #9)
 *   from external data are normalized so getChordNotes/buildChordString
 *   never silently drop them.
 */
function enforceChordInvariants(v3) {
    let { isDominant, extension, alterations } = v3;
    if (extension === '6') {
        isDominant = false;
    }
    if ((v3.quality === 'sus2' || v3.quality === 'sus4') && !isDominant) {
        extension = null;
    }
    if (alterations.some(a => a !== normalizeAlterationSymbol(a))) {
        alterations = alterations.map(normalizeAlterationSymbol);
    }
    if (isDominant === v3.isDominant && extension === v3.extension && alterations === v3.alterations) {
        return v3;
    }
    return { ...v3, isDominant, extension, alterations };
}
/**
 * Normalize any chord model to V2 format.
 *
 * If already V2, returns as-is. If V1, migrates to V2.
 * Useful for functions that want to work exclusively with V2 internally.
 *
 * @param chord - Chord model in either format
 * @returns V2 format chord model
 * @throws Error if V1 chord has invalid note strings
 *
 * @example
 * function processChord(chord: AnyChordObjectModel) {
 *   const v2 = normalizeToV2(chord);
 *   // Now work with v2 format exclusively
 * }
 */
export function normalizeToV2(chord) {
    if (isChordModelV2(chord)) {
        return chord;
    }
    // If V3, convert root→fundamental
    if (isChordModelV3(chord)) {
        return {
            fundamental: chord.root,
            quality: chord.quality,
            extension: chord.extension,
            isDominant: chord.isDominant,
            adds: [...chord.adds],
            alterations: [...chord.alterations],
            bass: chord.bass,
        };
    }
    return migrateChordToV2(chord);
}
// ============================================================================
// Comparison Functions
// ============================================================================
/**
 * Compare two chord models for equality.
 *
 * Works with any combination of V1, V2, and V3 models.
 * Normalizes both to V3 for consistent comparison.
 * Uses direct quality comparison (V3 always uses "major", never "").
 *
 * @param a - First chord model
 * @param b - Second chord model
 * @returns true if chords are equal, false otherwise
 */
export function equalChordModels(a, b) {
    // Normalize both to V3 for comparison
    const aV3 = normalizeToV3(a);
    const bV3 = normalizeToV3(b);
    // Compare root notes
    if (!equalNotesNullable(aV3.root, bV3.root)) {
        return false;
    }
    // Compare bass notes
    if (!equalNotesNullable(aV3.bass, bV3.bass)) {
        return false;
    }
    // Direct quality comparison (V3 uses canonical values)
    if (aV3.quality !== bV3.quality) {
        return false;
    }
    // Compare other fields
    return (aV3.extension === bV3.extension &&
        aV3.isDominant === bV3.isDominant &&
        arraysEqual(aV3.adds, bV3.adds) &&
        arraysEqual(aV3.alterations, bV3.alterations));
}
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Helper: Compare two arrays for equality.
 *
 * @param a - First array
 * @param b - Second array
 * @returns true if arrays are equal, false otherwise
 */
function arraysEqual(a, b) {
    if (a.length !== b.length)
        return false;
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i])
            return false;
    }
    return true;
}
